import React from 'react';
import { Tooltip, IconButton, Icon, IconButtonProps, TooltipProps, Box, styled, CircularProgress } from '@mui/material';
import { isString } from 'lodash';
import { generateNamesObject } from '../../utils';
import clsx from 'clsx';

export const iconButtonWithTooltipClasses = generateNamesObject(
    ['circularProgress', 'outlined'],
    'IconButtonWithTooltip',
);

const StyledTooltip = styled(Tooltip)(({ theme }) => ({
    cursor: 'pointer',
    [`& .${iconButtonWithTooltipClasses.circularProgress}`]: {
        padding: theme.spacing(1),
    },
    [`& .${iconButtonWithTooltipClasses.outlined}`]: {
        borderRadius: 6,
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
        width: 32,
        height: 32,
        '& svg': {
            width: 18,
        },
    },
}));

export type IconButtonWithTooltipProps = IconButtonProps & {
    tooltip: string;
    tooltipProps?: Partial<TooltipProps>;
    icon: string | React.ReactNode;
    isLoading?: boolean;
    variant?: 'outlined' | 'text';
};

export const IconButtonWithTooltip: React.FunctionComponent<IconButtonWithTooltipProps> = ({
    tooltip,
    tooltipProps,
    icon,
    isLoading,
    variant,
    ...rest
}) => {
    const button = isLoading ? (
        <CircularProgress className={iconButtonWithTooltipClasses.circularProgress} size={rest.size} />
    ) : (
        <IconButton
            aria-label={rest['aria-label'] || tooltip}
            {...rest}
            className={clsx({
                [iconButtonWithTooltipClasses.outlined]: variant === 'outlined',
            })}
        >
            {isString(icon) ? <Icon className={icon} fontSize="inherit" /> : icon}
        </IconButton>
    );

    if (!tooltip) {
        return button;
    }

    return (
        <StyledTooltip title={tooltip} {...tooltipProps}>
            <Box component="span">{button}</Box>
        </StyledTooltip>
    );
};
